import { Vue, Component, Ref, Provide, Emit } from 'vue-property-decorator'
import BillingModalEntityComponent from '@/modules/billing-module/shared/components/modal-entity/billing-modal-entity.vue'
import BillingModalEntity from '@/modules/billing-module/shared/components/modal-entity/billing-modal-entity'
import BillingModalInvoicePaymentCardComponent from '@/modules/billing-module/components/modal/invoice-payment/components/card/billing-modal-invoice-payment-card.vue'
import BillingModalInvoicePaymentFormComponent from '@/modules/billing-module/components/modal/invoice-payment/components/form/billing-modal-invoice-payment-form.vue'
import BillingModalInvoicePaymentActionsGridComponent from '@/modules/billing-module/components/modal/invoice-payment/components/actions/grid/billing-modal-invoice-payment-actions-grid.vue'
import BillingModalInvoicePaymentActionsGrid from '@/modules/billing-module/components/modal/invoice-payment/components/actions/grid/billing-modal-invoice-payment-actions-grid'
import BillingModalInvoicePaymentActionsModalComponent from '@/modules/billing-module/components/modal/invoice-payment/components/actions/modal/billing-modal-invoice-payment-actions-modal.vue'
import {
    BillingModalInvoicePaymentParams,
    BillingModalInvoicePaymentState,
    initialBillingModalInvoicePaymentState
} from '@/modules/billing-module/components/modal/invoice-payment/billing-modal-invoice-payment.model'
import { InvoicePaymentStatus } from '@/api/services/billing/invoice-payment/billing-invoice-payment.dto'
import { billingInvoicePaymentService } from '@/api/services/billing/invoice-payment/billing-invoice-payment.service'
import { cloneDeep } from 'lodash'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { GridUtils } from '@/shared/util/grid-utils'
import { CommonFilterModel } from '@/api/resources/common/shared/models/common.models'
import { BillingConfigInvoiceConceptsGridDatasource } from '@/modules/billing-module/shared/config/invoice-concepts-grid/billing-config-invoice-concepts-grid.datasource'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { BillingModalEntityParams } from '@/modules/billing-module/shared/components/modal-entity/billing-modal-entity-model'

@Component({
    components: {
        BillingModalEntityComponent,
        BillingModalInvoicePaymentCardComponent,
        BillingModalInvoicePaymentFormComponent,
        BillingModalInvoicePaymentActionsGridComponent,
        BillingModalInvoicePaymentActionsModalComponent
    }
})
export default class BillingModalInvoicePayment extends Vue {
    @Ref() readonly invoicePaymentModal!: BillingModalEntity<AppliedBillableConceptResponse>
    @Ref() readonly gridActions!: BillingModalInvoicePaymentActionsGrid

    @Provide() externalValidator = this.$validator

    @Emit()
    refresh(): void {
        // emits an event
    }

    state: BillingModalInvoicePaymentState
    constructor() {
        super()
        this.state = initialBillingModalInvoicePaymentState()
    }

    /// COMPUTED ///

    private get _invoicePaymentCancelled(): boolean {
        return this.state.invoicePayment?.status.code === InvoicePaymentStatus.CANCELLED
    }
    private get _invoicePaymentVerified(): boolean {
        return this.state.invoicePayment?.status.code === InvoicePaymentStatus.VERIFIED
    }

    private get _modalTitle(): string {
        return this.state.invoicePayment
            ? this.$tc('accounting.action.updateInvoicePayment')
            : this.$tc('accounting.action.generateInvoicePayment')
    }
    private static get _gridUiComponent(): string {
        return 'uicomponent.billing.grid.invoicePaymentModal'
    }

    /// selection
    private get _selectionDatasource(): BillingConfigInvoiceConceptsGridDatasource {
        const filterModel: CommonFilterModel = {}
        if (this.state.invoice) {
            filterModel['deliveryNote.invoice.id'] = GridUtils.getNumberFilterModel('equals', this.state.invoice.id)
        }
        filterModel['invoicePayment'] = GridUtils.getNumberFilterModel('equals', null)
        return new BillingConfigInvoiceConceptsGridDatasource(filterModel)
    }
    private get _selectionTitle(): string {
        return this.state.addToMode
            ? this.$tc('accounting.invoicePaymentModal.addConceptsTitle')
            : this.$tc('accounting.invoicePaymentModal.conceptSelectionTitle')
    }
    private get _selectionGridTitle(): string {
        return `${this.$tc('accounting.field.invoice')} ${this.state.invoice?.label ?? '-'}`
    }

    /// crud
    private get _crudDatasource(): BillingConfigInvoiceConceptsGridDatasource {
        const filterModel: CommonFilterModel = {}
        if (this.state.invoicePayment) {
            filterModel['invoicePayment.id'] = GridUtils.getNumberFilterModel('equals', this.state.invoicePayment.id)
        }
        return new BillingConfigInvoiceConceptsGridDatasource(filterModel)
    }
    private get _crudGridKey(): number {
        return this.state.invoicePayment?.appliedBillableConceptCount ?? 0
    }
    private get _crudGridTitle(): string {
        return `${this.$tc('accounting.invoicePaymentModal.invoicePaymentConcepts')} (${this.state.conceptsTotalCount})`
    }
    private get _crudEmptyStateTitle(): string {
        return this.$tc('accounting.invoicePaymentModal.invoicePaymentWithoutConcepts')
    }

    get modalEntityParams(): BillingModalEntityParams<AppliedBillableConceptResponse> {
        return {
            modalTitle: this._modalTitle,
            selectionMode: this.state.selectionMode,
            selection: {
                datasource: this._selectionDatasource,
                addToMode: this.state.addToMode,
                title: this._selectionTitle,
                gridTitle: this._selectionGridTitle,
                gridUiComponent: BillingModalInvoicePayment._gridUiComponent
            },
            crud: {
                datasource: this._crudDatasource,
                gridKey: `${this._crudGridKey}`,
                visibleGrid: true,
                gridTitle: this._crudGridTitle,
                gridUiComponent: BillingModalInvoicePayment._gridUiComponent,
                getContextMenuItems: this.getCrudContextMenuItems,
                emptyStateTitle: this._crudEmptyStateTitle
            }
        }
    }

    get visibleForm(): boolean {
        return this._invoicePaymentCancelled || this._invoicePaymentVerified
    }

    /// MODAL ///

    show(params: BillingModalInvoicePaymentParams): void {
        this.state = initialBillingModalInvoicePaymentState()

        const { invoice, invoicePayment } = params
        if (invoice) {
            this.state.invoice = cloneDeep(invoice)
        }
        if (invoicePayment) {
            this.state.invoicePayment = cloneDeep(invoicePayment)
            this.state.selectionMode = false
        }

        this.invoicePaymentModal.show()
    }
    hide(): void {
        this.invoicePaymentModal.hide()
    }

    /// SERVICES CALLS ///

    private async _reloadInvoicePayment(invoicePaymentId: number): Promise<boolean> {
        const response =
            await PromiseUtils.toCallSecure(
                billingInvoicePaymentService.get(store.state.labId, invoicePaymentId))
        if (response !== null) {
            this.state.invoicePayment = response
        }
        return response !== null
    }

    /// EVENT HANDLERS ///

    onConceptsAdded(): void {
        this.state.selectionMode = true
        this.state.addToMode = true
    }
    onHideAddToMode(): void {
        this.state.selectionMode = false
        this.state.addToMode = false
    }

    onConceptsTotalCountChanged(totalCount: number): void {
        this.state.conceptsTotalCount = totalCount
    }
    onConceptsSelected(appliedBillableConcepts: AppliedBillableConceptResponse[]): void {
        this.state.appliedBillableConcepts = appliedBillableConcepts
    }

    async onRefresh(invoicePaymentId: number): Promise<void> {
        if (await this._reloadInvoicePayment(invoicePaymentId)) {
            this.state.selectionMode = false
            this.refresh() // emits an event
        }
    }

    getCrudContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return this.gridActions?.getContextMenuItems(params) ?? []
    }
}
